<script setup lang="ts">
import Modal from '~/components/common/ModalV2.vue'
import { useModalStore } from '~/stores/modal'
import DatePickerWrapper from '~/components/DatePickerWrapper.vue'
import CheckboxesFilter from '~/components/shared/price-assessments/ChartCheckboxesFilter.vue'
import Dropdown from '~/components/shared/common/Dropdown.vue'
import CommonButton from '~/components/common/Button.vue'
import cloneDeep from 'lodash/cloneDeep'
import type { TGeneral } from '~/src/types/common'
import { getNewFilter, getSelectedOptions } from '~/utils/chart-tab'

const props = defineProps<{
  catalog: TGeneral[]
  combinations: TGeneral[]
  mineral: string
  minDate: Date
  startDate: string
  endDate: string
  authorized: boolean
  timeLineIndicator: string
}>()

const selectedCurrency = defineModel<string>('selectedCurrency')

const emit = defineEmits(['updateSelectedDates', 'applyFilters'])

const modalStore = useModalStore()

const filters = ref([])
const previousFilters = ref([])

const isOpen = computed(() => modalStore.modals.priceAssessmentFiltersOpen)

const applyFilters = () => {
  const selectedOptions = getSelectedOptions(filters.value)
  emit('applyFilters', selectedOptions)
}

const resetFilters = () => {
  filters.value = cloneDeep(props.catalog)

  const selectedOptions = getSelectedOptions(filters.value)

  emit('applyFilters', selectedOptions)
}

const updateFilters = () => {
  const selectedOptions = getSelectedOptions(filters.value)

  filters.value = filters.value.map((filter) =>
    getNewFilter({
      filter,
      combinations: props.combinations,
      selectedOptions,
    }),
  )

  validateParentCheck()
}

const updatedHeader = (header: { name: string; value: boolean }) => {
  filters.value = [...filters.value].map((filter) => {
    const filterName = filter.label ?? filter.name

    if (filterName !== header.name) return filter

    return {
      ...filter,
      checked: header.value,
      options: filter.options.map((option) => ({
        ...option,
        checked: header.value,
      })),
    }
  })

  updateFilters()
}

const changeOptions = (option: {
  level: string
  name: string
  value: boolean
}) => {
  filters.value = filters.value.map((filter) => {
    const levelName = filter.label ?? filter.name

    if (levelName !== option.level) return filter

    const options = filter.options.map((op) => {
      const optionName = op.data.name
      return optionName !== option.name ? op : { ...op, checked: option.value }
    })

    return { ...filter, options }
  })
  updateFilters()
}

const validateParentCheck = () => {
  filters.value = [...filters.value].map((filter) => {
    const allDisabled = filter.options.every((option) => option.disabled)

    if (allDisabled) return { ...filter, disabled: allDisabled }

    const isChecked = [...filter.options]
      .filter((option) => !option.disabled)
      .every((option) => option.checked)

    return { ...filter, checked: isChecked }
  })
}

const closeModal = () => {
  modalStore.close('priceAssessmentFilters')
}

onMounted(() => {
  filters.value = cloneDeep(props.catalog)
  previousFilters.value = cloneDeep(props.catalog)

  if (props.combinations && props.combinations.length) {
    updateFilters()
  }

  previousFilters.value = cloneDeep(filters.value)
  applyFilters()
})
</script>

<template>
  <Modal
    :is-open="isOpen"
    name="priceAssessmentFilters"
    title="Filters"
    modal-classes="pt-8 pb-0 px-0 w-full max-w-[540px] overflow-y-clip h-[85vh] flex flex-col"
    button-close-classes="left-5 right-auto top-5"
    content-classes="h-[calc(100%_-_30px)] overflow-y-auto px-4 pt-4 lg:px-16 lg:pt-8"
    @close="closeModal"
  >
    <div>
      <!-- Date filter -->
      <div class="input relative mt-10 w-full">
        <DatePickerWrapper
          data-testid="start-date"
          :min-date="minDate"
          :start-date="startDate"
          :end-date="endDate"
          :default-value="timeLineIndicator"
          @date-range-updated="emit('updateSelectedDates', $event)"
        />
      </div>

      <Dropdown
        v-model="selectedCurrency"
        class="mt-5 bg-card border-0"
        :options="['CNY', 'USD']"
      />

      <div class="py-4">
        <div class="w-full border-t border-s-300" />
      </div>
    </div>

    <!-- Mineral properties filters -->
    <div v-for="filter in filters" :key="filter.name">
      <CheckboxesFilter
        :filter="filter"
        :is-modal="true"
        @change-options="changeOptions"
        @updated-header="updatedHeader"
      />
      <div class="py-4">
        <div class="w-full border-t border-s-300" />
      </div>
    </div>

    <template #footer>
      <div
        class="flex justify-end px-4 py-6 shadow-[0px_-4px_10px_rgba(0,0,0,0.1)]"
      >
        <div class="flex w-1/2 justify-start">
          <button class="flex items-center px-2.5 py-1.5" @click="resetFilters">
            <span class="text-sm font-semibold underline">Clear all</span>
          </button>
        </div>
        <div class="flex w-1/2 justify-end">
          <CommonButton
            class="border-0 px-6 text-sm"
            variant="primary"
            @click="applyFilters"
          >
            Apply
          </CommonButton>
        </div>
      </div>
    </template>
  </Modal>
</template>
